import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/gestionale/integrations/supabase/client";
import { useAuth } from "@/gestionale/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/gestionale/components/ui/card";
import { Badge } from "@/gestionale/components/ui/badge";
import { Button } from "@/gestionale/components/ui/button";
import { Checkbox } from "@/gestionale/components/ui/checkbox";
import { Input } from "@/gestionale/components/ui/input";
import { Label } from "@/gestionale/components/ui/label";
import { ShieldCheck, ShieldX, Minus, Plus, ChevronDown, ChevronUp, Trophy, Users, MessageSquare, Trash2 } from "lucide-react";
import { format, parseISO, isBefore, addDays } from "date-fns";
import { it } from "date-fns/locale";
import { motion } from "framer-motion";
import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/gestionale/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/gestionale/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/gestionale/components/ui/tabs";
import GeneraStatisticaButton from "@/gestionale/components/GeneraStatisticaButton";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" as const } },
};

export default function AllenatoreDashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Sessione/partita aperta
  const [sessioneAperta, setSessioneAperta] = useState<string | null>(null);
  const [partitaAperta, setPartitaAperta] = useState<string | null>(null);

  // Dialog nuova sessione allenamento
  const [showSessioneDialog, setShowSessioneDialog] = useState(false);
  const [sessioneData, setSessioneData] = useState(format(new Date(), "yyyy-MM-dd"));
  const [sessioneNote, setSessioneNote] = useState("");

  // Dialog nuova partita
  const [showPartitaDialog, setShowPartitaDialog] = useState(false);
  const [partitaData, setPartitaData] = useState(format(new Date(), "yyyy-MM-dd"));
  const [partitaAvversario, setPartitaAvversario] = useState("");
  const [partitaCasaTrasferta, setPartitaCasaTrasferta] = useState("Casa");
  const [partitaTipo, setPartitaTipo] = useState("Campionato");
  const [partitaGolFatti, setPartitaGolFatti] = useState(0);
  const [partitaGolSubiti, setPartitaGolSubiti] = useState(0);
  const [partitaNote, setPartitaNote] = useState("");

  // Dialog comunicazione
  const [showComunicazioneDialog, setShowComunicazioneDialog] = useState(false);
  const [commOggetto, setCommOggetto] = useState("");
  const [commMessaggio, setCommMessaggio] = useState("");

  // Persona collegata
  const { data: persona } = useQuery({
    queryKey: ["allenatore-persona", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from("persone").select("*").eq("user_id", user!.id).maybeSingle();
      return data;
    },
  });

  // Giocatori (persone con ruolo Giocatore)
  const { data: giocatori = [] } = useQuery({
    queryKey: ["allenatore-giocatori"],
    enabled: !!persona,
    queryFn: async () => {
      const { data: ruoliData } = await supabase
        .from("ruoli")
        .select("persona_id")
        .eq("tipo_ruolo", "Giocatore");
      const ids = (ruoliData || []).map((r: any) => r.persona_id);
      if (ids.length === 0) return [];
      const { data } = await supabase
        .from("persone")
        .select("id, nome, cognome, certificato_medico_scadenza")
        .in("id", ids)
        .order("cognome");
      return data || [];
    },
  });

  const giocatoriIds = giocatori.map((g: any) => g.id);

  // Sessioni allenamento (senza corso_id = sessioni calcio)
  const { data: sessioni = [] } = useQuery({
    queryKey: ["allenatore-sessioni"],
    enabled: !!persona,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sessioni_allenamento")
        .select("*")
        .is("corso_id", null)
        .order("data", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const sessioneIds = sessioni.map((s: any) => s.id);

  // Presenze per tutte le sessioni
  const { data: presenze = [] } = useQuery({
    queryKey: ["allenatore-presenze", sessioneIds.join(",")],
    enabled: sessioneIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("presenze")
        .select("*")
        .in("sessione_id", sessioneIds);
      if (error) throw error;
      return data || [];
    },
  });

  // Partite
  const { data: partite = [] } = useQuery({
    queryKey: ["allenatore-partite"],
    enabled: !!persona,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partite")
        .select("*")
        .order("data", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const partiteIds = partite.map((p: any) => p.id);

  // Statistiche partite
  const { data: statistiche = [] } = useQuery({
    queryKey: ["allenatore-statistiche", partiteIds.join(",")],
    enabled: partiteIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("statistiche_partita")
        .select("*")
        .in("partita_id", partiteIds);
      if (error) throw error;
      return data || [];
    },
  });

  // Comunicazioni
  const { data: comunicazioni = [] } = useQuery({
    queryKey: ["allenatore-comunicazioni", persona?.id],
    enabled: !!persona,
    queryFn: async () => {
      const { data: destData } = await supabase
        .from("comunicazioni_destinatari")
        .select("comunicazione_id")
        .eq("persona_id", persona!.id);
      const commIds = (destData || []).map((d: any) => d.comunicazione_id);
      const { data } = await supabase
        .from("comunicazioni")
        .select("*")
        .or(
          [
            commIds.length > 0 ? `id.in.(${commIds.join(",")})` : null,
            `tipo_destinatari.eq.tutti`,
          ].filter(Boolean).join(",")
        )
        .order("created_at", { ascending: false })
        .limit(10);
      return data || [];
    },
  });

  // Mutations
  const creaSessioneMutation = useMutation({
    mutationFn: async ({ data, note }: { data: string; note: string }) => {
      const { data: newSessione, error } = await supabase
        .from("sessioni_allenamento")
        .insert({ corso: "Allenamento", data, note: note || null })
        .select()
        .single();
      if (error) throw error;
      if (giocatoriIds.length > 0) {
        await supabase.from("presenze").insert(
          giocatoriIds.map((id: string) => ({ sessione_id: newSessione.id, persona_id: id, presente: false }))
        );
      }
      return newSessione;
    },
    onSuccess: (newSessione) => {
      queryClient.invalidateQueries({ queryKey: ["allenatore-sessioni"] });
      toast.success("Sessione creata");
      setShowSessioneDialog(false);
      setSessioneData(format(new Date(), "yyyy-MM-dd"));
      setSessioneNote("");
      setSessioneAperta(newSessione.id);
    },
    onError: (e: any) => toast.error("Errore: " + e.message),
  });

  const eliminaSessioneMutation = useMutation({
    mutationFn: async (sessioneId: string) => {
      await supabase.from("presenze").delete().eq("sessione_id", sessioneId);
      const { error } = await supabase.from("sessioni_allenamento").delete().eq("id", sessioneId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allenatore-sessioni"] });
      toast.success("Sessione eliminata");
      setSessioneAperta(null);
    },
    onError: (e: any) => toast.error("Errore: " + e.message),
  });

  const togglePresenzaMutation = useMutation({
    mutationFn: async ({ presenzaId, presente }: { presenzaId: string; presente: boolean }) => {
      const { error } = await supabase.from("presenze").update({ presente }).eq("id", presenzaId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allenatore-presenze"] });
    },
    onError: (e: any) => toast.error("Errore: " + e.message),
  });

  const creaPartitaMutation = useMutation({
    mutationFn: async () => {
      const { data: newPartita, error } = await supabase
        .from("partite")
        .insert({
          data: partitaData,
          avversario: partitaAvversario,
          casa_trasferta: partitaCasaTrasferta,
          tipo: partitaTipo,
          gol_fatti: partitaGolFatti,
          gol_subiti: partitaGolSubiti,
          note: partitaNote || null,
        })
        .select()
        .single();
      if (error) throw error;
      if (giocatoriIds.length > 0) {
        await supabase.from("statistiche_partita").insert(
          giocatoriIds.map((id: string) => ({ partita_id: newPartita.id, persona_id: id }))
        );
      }
      return newPartita;
    },
    onSuccess: (newPartita) => {
      queryClient.invalidateQueries({ queryKey: ["allenatore-partite"] });
      toast.success("Partita creata");
      setShowPartitaDialog(false);
      setPartitaAvversario("");
      setPartitaGolFatti(0);
      setPartitaGolSubiti(0);
      setPartitaNote("");
      setPartitaAperta(newPartita.id);
    },
    onError: (e: any) => toast.error("Errore: " + e.message),
  });

  const eliminaPartitaMutation = useMutation({
    mutationFn: async (partitaId: string) => {
      await supabase.from("statistiche_partita").delete().eq("partita_id", partitaId);
      const { error } = await supabase.from("partite").delete().eq("id", partitaId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allenatore-partite"] });
      toast.success("Partita eliminata");
      setPartitaAperta(null);
    },
    onError: (e: any) => toast.error("Errore: " + e.message),
  });

  const updateStatisticaMutation = useMutation({
    mutationFn: async ({ statId, field, value }: { statId: string; field: string; value: number | boolean }) => {
      const { error } = await supabase.from("statistiche_partita").update({ [field]: value }).eq("id", statId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allenatore-statistiche"] });
    },
    onError: (e: any) => toast.error("Errore: " + e.message),
  });

  const inviaComunicazioneMutation = useMutation({
    mutationFn: async () => {
      if (giocatoriIds.length === 0) throw new Error("Nessun giocatore registrato");
      const { data: com, error: comErr } = await supabase
        .from("comunicazioni")
        .insert({ oggetto: commOggetto, messaggio: commMessaggio, tipo_destinatari: "corso", corso_filtro: "Giocatori" })
        .select()
        .single();
      if (comErr) throw comErr;
      const { error: destErr } = await supabase
        .from("comunicazioni_destinatari")
        .insert(giocatoriIds.map((id: string) => ({ comunicazione_id: com.id, persona_id: id })));
      if (destErr) throw destErr;
      return { count: giocatoriIds.length };
    },
    onSuccess: (result) => {
      toast.success(`Comunicazione inviata a ${result.count} giocatori`);
      setShowComunicazioneDialog(false);
      setCommOggetto("");
      setCommMessaggio("");
      queryClient.invalidateQueries({ queryKey: ["allenatore-comunicazioni"] });
    },
    onError: (e: any) => toast.error("Errore: " + e.message),
  });

  if (!persona) {
    return (
      <div className="space-y-8">
        <h1 className="text-3xl font-bold tracking-tight">Area Allenatore</h1>
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Il tuo account non è ancora collegato a un profilo. Contatta la segreteria.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const today = new Date();
  const warningDate = addDays(today, 30);
  const certAlert = giocatori.filter((g: any) => {
    if (!g.certificato_medico_scadenza) return false;
    return isBefore(parseISO(g.certificato_medico_scadenza), warningDate);
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Area Allenatore</h1>
        <p className="text-muted-foreground mt-1">Benvenuto, {persona.nome} {persona.cognome}</p>
      </div>

      <motion.div className="space-y-6" variants={container} initial="hidden" animate="show">

        {/* Alert certificati */}
        {certAlert.length > 0 && (
          <motion.div variants={item}>
            <Card className="border-amber-500/50 bg-amber-500/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                  <ShieldX className="h-5 w-5" />
                  Certificati Medici in scadenza o scaduti ({certAlert.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {certAlert.map((g: any) => {
                  const scaduto = isBefore(parseISO(g.certificato_medico_scadenza), today);
                  return (
                    <div key={g.id} className="flex items-center justify-between p-3 rounded-lg bg-background text-sm">
                      <span className="font-medium">{g.cognome} {g.nome}</span>
                      <Badge variant={scaduto ? "destructive" : "outline"} className={!scaduto ? "border-amber-500 text-amber-700" : ""}>
                        {scaduto ? "Scaduto" : "In scadenza"} · {format(parseISO(g.certificato_medico_scadenza), "dd/MM/yyyy")}
                      </Badge>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </motion.div>
        )}

        <Tabs defaultValue="giocatori">
          <TabsList className="mb-4">
            <TabsTrigger value="giocatori"><Users className="h-4 w-4 mr-2" />Rosa</TabsTrigger>
            <TabsTrigger value="allenamenti">Allenamenti</TabsTrigger>
            <TabsTrigger value="partite"><Trophy className="h-4 w-4 mr-2" />Partite</TabsTrigger>
            <TabsTrigger value="comunicazioni"><MessageSquare className="h-4 w-4 mr-2" />Comunicazioni</TabsTrigger>
          </TabsList>

          {/* Rosa */}
          <TabsContent value="giocatori">
            <motion.div variants={item}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    Giocatori <Badge variant="outline">{giocatori.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {giocatori.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nessun giocatore registrato.</p>
                  ) : (
                    giocatori.map((g: any) => {
                      const cert = g.certificato_medico_scadenza;
                      const scaduto = cert ? isBefore(parseISO(cert), today) : null;
                      const inScadenza = cert ? isBefore(parseISO(cert), warningDate) : null;

                      // Statistiche aggregate giocatore
                      const statGiocatore = statistiche.filter((s: any) => s.persona_id === g.id);
                      const partiteGiocate = statGiocatore.filter((s: any) => s.minuti_giocati > 0).length;
                      const totalGoal = statGiocatore.reduce((sum: number, s: any) => sum + (s.goal || 0), 0);
                      const totalAmm = statGiocatore.reduce((sum: number, s: any) => sum + (s.ammonizioni || 0), 0);
                      const totalEsp = statGiocatore.reduce((sum: number, s: any) => sum + (s.espulsioni || 0), 0);
                      const presenzeGiocatore = presenze.filter((p: any) => p.persona_id === g.id && p.presente).length;

                      return (
                        <div key={g.id} className="p-3 rounded-lg bg-muted/50 text-sm">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">{g.cognome} {g.nome}</span>
                            {cert === null ? (
                              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Minus className="h-4 w-4" /> N/D</span>
                            ) : scaduto ? (
                              <Badge variant="destructive" className="text-xs">Scaduto · {format(parseISO(cert), "dd/MM/yyyy")}</Badge>
                            ) : inScadenza ? (
                              <Badge variant="outline" className="text-xs border-amber-500 text-amber-700">In scadenza · {format(parseISO(cert), "dd/MM/yyyy")}</Badge>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600"><ShieldCheck className="h-4 w-4" /> {format(parseISO(cert), "dd/MM/yyyy")}</span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex gap-4 text-xs text-muted-foreground">
                              <span>⚽ {totalGoal} goal</span>
                              <span>🏃 {partiteGiocate} partite</span>
                              <span>🟨 {totalAmm}</span>
                              <span>🟥 {totalEsp}</span>
                              <span>📋 {presenzeGiocatore} allenamenti</span>
                            </div>
                            <GeneraStatisticaButton persona={g} tipo="giocatore" />
                          </div>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Allenamenti */}
          <TabsContent value="allenamenti">
            <motion.div variants={item}>
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      Sessioni <Badge variant="outline">{sessioni.length}</Badge>
                    </CardTitle>
                    <Button size="sm" onClick={() => setShowSessioneDialog(true)}>
                      <Plus className="h-4 w-4 mr-1" /> Nuova sessione
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {sessioni.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nessuna sessione registrata.</p>
                  ) : (
                    sessioni.map((s: any) => {
                      const isOpen = sessioneAperta === s.id;
                      const presenzeSessione = presenze.filter((p: any) => p.sessione_id === s.id);
                      const presentiCount = presenzeSessione.filter((p: any) => p.presente).length;
                      return (
                        <div key={s.id} className="rounded-lg border">
                          <div className="flex items-center">
                            <button
                              className="flex-1 flex items-center justify-between p-3 text-sm hover:bg-muted/50 transition-colors"
                              onClick={() => setSessioneAperta(isOpen ? null : s.id)}
                            >
                              <div className="flex items-center gap-3">
                                <span className="font-medium">{format(parseISO(s.data), "dd/MM/yyyy")}</span>
                                {s.note && <span className="text-muted-foreground text-xs">{s.note}</span>}
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">{presentiCount}/{giocatori.length} presenti</Badge>
                                {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </div>
                            </button>
                            <button
                              className="p-3 text-destructive hover:bg-destructive/10 transition-colors"
                              onClick={() => { if (confirm("Eliminare questa sessione?")) eliminaSessioneMutation.mutate(s.id); }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          {isOpen && (
                            <div className="border-t p-3 space-y-2">
                              {giocatori.map((g: any) => {
                                const presenza = presenzeSessione.find((p: any) => p.persona_id === g.id);
                                return (
                                  <div key={g.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50">
                                    <Checkbox
                                      checked={presenza?.presente || false}
                                      onCheckedChange={(checked) => {
                                        if (presenza) togglePresenzaMutation.mutate({ presenzaId: presenza.id, presente: !!checked });
                                      }}
                                      disabled={!presenza || togglePresenzaMutation.isPending}
                                    />
                                    <span className="text-sm font-medium">{g.cognome} {g.nome}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Partite */}
          <TabsContent value="partite">
            <motion.div variants={item}>
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      Partite <Badge variant="outline">{partite.length}</Badge>
                    </CardTitle>
                    <Button size="sm" onClick={() => setShowPartitaDialog(true)}>
                      <Plus className="h-4 w-4 mr-1" /> Nuova partita
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {partite.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nessuna partita registrata.</p>
                  ) : (
                    partite.map((p: any) => {
                      const isOpen = partitaAperta === p.id;
                      const statPartita = statistiche.filter((s: any) => s.partita_id === p.id);
                      const risultato = `${p.gol_fatti} - ${p.gol_subiti}`;
                      return (
                        <div key={p.id} className="rounded-lg border">
                          <div className="flex items-center">
                            <button
                              className="flex-1 flex items-center justify-between p-3 text-sm hover:bg-muted/50 transition-colors"
                              onClick={() => setPartitaAperta(isOpen ? null : p.id)}
                            >
                              <div className="flex items-center gap-3">
                                <span className="font-medium">{format(parseISO(p.data), "dd/MM/yyyy")}</span>
                                <span>{p.casa_trasferta} vs {p.avversario}</span>
                                <Badge variant="outline" className="text-xs">{p.tipo}</Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={`text-xs font-bold ${p.gol_fatti > p.gol_subiti ? "border-green-500 text-green-700" : p.gol_fatti < p.gol_subiti ? "border-red-500 text-red-700" : ""}`}>
                                  {risultato}
                                </Badge>
                                {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </div>
                            </button>
                            <button
                              className="p-3 text-destructive hover:bg-destructive/10 transition-colors"
                              onClick={() => { if (confirm("Eliminare questa partita?")) eliminaPartitaMutation.mutate(p.id); }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          {isOpen && (
                            <div className="border-t p-3">
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-muted-foreground">
                                      <th className="text-left p-2">Giocatore</th>
                                      <th className="text-center p-2">Titolare</th>
                                      <th className="text-center p-2">Min</th>
                                      <th className="text-center p-2">⚽</th>
                                      <th className="text-center p-2">🟨</th>
                                      <th className="text-center p-2">🟥</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {giocatori.map((g: any) => {
                                      const stat = statPartita.find((s: any) => s.persona_id === g.id);
                                      if (!stat) return null;
                                      return (
                                        <tr key={g.id} className="border-t hover:bg-muted/30">
                                          <td className="p-2 font-medium">{g.cognome} {g.nome}</td>
                                          <td className="p-2 text-center">
                                            <Checkbox
                                              checked={stat.titolare}
                                              onCheckedChange={(v) => updateStatisticaMutation.mutate({ statId: stat.id, field: "titolare", value: !!v })}
                                            />
                                          </td>
                                          <td className="p-2 text-center">
                                            <input
                                              type="number" min={0} max={120}
                                              className="w-12 text-center border rounded p-1 bg-background text-xs"
                                              defaultValue={stat.minuti_giocati}
                                              onBlur={(e) => updateStatisticaMutation.mutate({ statId: stat.id, field: "minuti_giocati", value: parseInt(e.target.value) || 0 })}
                                            />
                                          </td>
                                          <td className="p-2 text-center">
                                            <input
                                              type="number" min={0}
                                              className="w-10 text-center border rounded p-1 bg-background text-xs"
                                              defaultValue={stat.goal}
                                              onBlur={(e) => updateStatisticaMutation.mutate({ statId: stat.id, field: "goal", value: parseInt(e.target.value) || 0 })}
                                            />
                                          </td>
                                          <td className="p-2 text-center">
                                            <input
                                              type="number" min={0} max={2}
                                              className="w-10 text-center border rounded p-1 bg-background text-xs"
                                              defaultValue={stat.ammonizioni}
                                              onBlur={(e) => updateStatisticaMutation.mutate({ statId: stat.id, field: "ammonizioni", value: parseInt(e.target.value) || 0 })}
                                            />
                                          </td>
                                          <td className="p-2 text-center">
                                            <input
                                              type="number" min={0} max={1}
                                              className="w-10 text-center border rounded p-1 bg-background text-xs"
                                              defaultValue={stat.espulsioni}
                                              onBlur={(e) => updateStatisticaMutation.mutate({ statId: stat.id, field: "espulsioni", value: parseInt(e.target.value) || 0 })}
                                            />
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Comunicazioni */}
          <TabsContent value="comunicazioni">
            <motion.div variants={item}>
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-primary" />
                      Comunicazioni
                    </CardTitle>
                    <Button size="sm" onClick={() => setShowComunicazioneDialog(true)}>
                      <Plus className="h-4 w-4 mr-1" /> Nuova
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {comunicazioni.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nessuna comunicazione</p>
                  ) : (
                    comunicazioni.map((c: any) => (
                      <div key={c.id} className="p-4 rounded-lg bg-muted/50 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{c.oggetto}</span>
                          <span className="text-xs text-muted-foreground">
                            {format(parseISO(c.created_at), "dd/MM/yyyy", { locale: it })}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground whitespace-pre-line">{c.messaggio}</p>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* Dialog nuova sessione */}
      <Dialog open={showSessioneDialog} onOpenChange={(open) => { if (!open) setShowSessioneDialog(false); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuova Sessione di Allenamento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Data *</Label>
              <Input type="date" value={sessioneData} onChange={(e) => setSessioneData(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Input value={sessioneNote} onChange={(e) => setSessioneNote(e.target.value)} placeholder="Opzionale" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSessioneDialog(false)}>Annulla</Button>
            <Button onClick={() => creaSessioneMutation.mutate({ data: sessioneData, note: sessioneNote })} disabled={creaSessioneMutation.isPending || !sessioneData}>
              Crea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog nuova partita */}
      <Dialog open={showPartitaDialog} onOpenChange={(open) => { if (!open) setShowPartitaDialog(false); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuova Partita</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Data *</Label>
              <Input type="date" value={partitaData} onChange={(e) => setPartitaData(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Avversario *</Label>
              <Input value={partitaAvversario} onChange={(e) => setPartitaAvversario(e.target.value)} placeholder="Es. A.S.D. Rivale" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Casa/Trasferta</Label>
                <Select value={partitaCasaTrasferta} onValueChange={setPartitaCasaTrasferta}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Casa">Casa</SelectItem>
                    <SelectItem value="Trasferta">Trasferta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={partitaTipo} onValueChange={setPartitaTipo}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Campionato">Campionato</SelectItem>
                    <SelectItem value="Coppa">Coppa</SelectItem>
                    <SelectItem value="Amichevole">Amichevole</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Gol fatti</Label>
                <Input type="number" min={0} value={partitaGolFatti} onChange={(e) => setPartitaGolFatti(parseInt(e.target.value) || 0)} />
              </div>
              <div className="space-y-2">
                <Label>Gol subiti</Label>
                <Input type="number" min={0} value={partitaGolSubiti} onChange={(e) => setPartitaGolSubiti(parseInt(e.target.value) || 0)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Input value={partitaNote} onChange={(e) => setPartitaNote(e.target.value)} placeholder="Opzionale" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPartitaDialog(false)}>Annulla</Button>
            <Button onClick={() => creaPartitaMutation.mutate()} disabled={creaPartitaMutation.isPending || !partitaData || !partitaAvversario}>
              Crea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog comunicazione */}
      <Dialog open={showComunicazioneDialog} onOpenChange={(open) => { if (!open) setShowComunicazioneDialog(false); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuova Comunicazione ai Giocatori</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Oggetto *</Label>
              <Input value={commOggetto} onChange={(e) => setCommOggetto(e.target.value)} placeholder="Es. Cambio orario allenamento" />
            </div>
            <div className="space-y-2">
              <Label>Messaggio *</Label>
              <textarea
                className="w-full border rounded-md p-2 text-sm bg-background min-h-[100px] resize-none"
                value={commMessaggio}
                onChange={(e) => setCommMessaggio(e.target.value)}
                placeholder="Scrivi il messaggio..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComunicazioneDialog(false)}>Annulla</Button>
            <Button onClick={() => inviaComunicazioneMutation.mutate()} disabled={inviaComunicazioneMutation.isPending || !commOggetto.trim() || !commMessaggio.trim()}>
              {inviaComunicazioneMutation.isPending ? "Invio..." : "Invia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
